import { EventEmitter, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { of } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AuthenticationService } from 'core/authentication/authentication.service';
import { environment } from '../../../environments/environment';
import { SignalrConnectionType } from '../../modules/shared/entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../../modules/shared/entities/signalrMethodName.enum';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {

  private hubConnection: signalR.HubConnection;
  private path: string;
  connectionId: string | null = null;

  validateOrderMessageReceived$ = new EventEmitter<any>();
  saveUploadedProductsReceived$ = new EventEmitter<any>();
  itemizeSetProgressReceived$ = new EventEmitter<any>();
  updatePricingProgressReceived$ = new EventEmitter<any>();
  editQuoteProgressReceived$ = new EventEmitter<any>();
  editQuoteForCalculateTotalsProgressReceived$ = new EventEmitter<any>();
  receiveOnlineUpdatesProgressReceived$ = new EventEmitter<any>();
  startedGoOnlineSyncProgressReceived$ = new EventEmitter<any>();
  signalrError$ = new EventEmitter<any>();
  importQuotesOrOrdersData$ = new EventEmitter<any>();
  generateQuoteProgressReceived$ = new EventEmitter<any>();

  constructor(
    private appInfoService: AppInfoService,
    private authService: AuthenticationService,
    private appStateService: AppStateService
  ) { }

  protected createBuilder(): signalR.HubConnectionBuilder {
    return new signalR.HubConnectionBuilder();
  }

  startConnection = async (connectionType: SignalrConnectionType, useAccessToken?, fullPath?): Promise<ReturnType<typeof of>> => {
    this.setPath(fullPath);

    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      await this.hubConnection.stop();
    }

    if (!useAccessToken) {
      this.hubConnection = this.createBuilder()
        .withUrl(this.path)
        .withAutomaticReconnect()
        .build();
    } else {
      this.hubConnection = this.createBuilder()
        .withUrl(`${this.path}/?userId=${this.appInfoService.userData.UserInfo.EmployeeId}`, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          accessTokenFactory: () => this.appStateService.state.isOnline
            ? (this.authService?.existingToken?.accessToken ?? null)
            : null
        })
        .withAutomaticReconnect()
        .build();
    }

    this.hubConnection.onclose(error => {
      if (error) {
        this.signalrError$.emit(error);
      }
    });

    this.hubConnection.onreconnected(() => {
      this.connectionId = this.hubConnection.connectionId;
    });

    this.subscribeToHubProxy(connectionType);
    return this.hubConnection
      .start()
      .then(() => {
        this.connectionId = this.hubConnection.connectionId;
        return of();
      })
      .catch(e => {
        console.error(this.appInfoService.appStrings.order_errorMessage);
        throw e;
      });
  };

  private subscribeToHubProxy(connectionType: SignalrConnectionType) {
    if (connectionType === SignalrConnectionType.ValidateOrder) {
      this.addOrderValidateListener();
    }
    if (connectionType === SignalrConnectionType.SaveUploadedProducts) {
      this.addUploadExcelListener();
    }

    if (connectionType === SignalrConnectionType.ItemizeSet) {
      this.addItemizeSetListener();
    }

    if (connectionType === SignalrConnectionType.UpdatePricingOnExpiredQuote) {
      this.addUpdatePricingListener();
    }

    if (connectionType === SignalrConnectionType.EditQuote) {
      this.addEditQuoteListener();
    }

    if (connectionType === SignalrConnectionType.EditQuoteForCalculateTotals) {
      this.addEditQuoteListenerForCalculateTotals();
    }

    if (connectionType === SignalrConnectionType.StartGoOnlineSync) {
      this.addStartGoOnlineListener();
    }

    if (connectionType === SignalrConnectionType.ReceiveOnlineUpdates) {
      this.addReceiveOnlineUpdatesListener();
    }

    if (connectionType === SignalrConnectionType.ImportQuotesOrOrders) {
      this.addImportQuoteOrOrderDataSetListener();
    }

    if (connectionType === SignalrConnectionType.GenerateQuote) {
      this.addGenerateQuoteListener();
    }
  }

  private addOrderValidateListener = () => {
    this.hubConnection.on('validate', data => {
      this.validateOrderMessageReceived$.emit(data);
    });
  };

  private addUploadExcelListener = () => {
    this.hubConnection.on(SignalrMethodName.saveUploadedProducts, data => this.saveUploadedProductsReceived$.emit(data));
  };

  private addItemizeSetListener = () => {
    this.hubConnection.on(SignalrMethodName.itemizeSet, data => this.itemizeSetProgressReceived$.emit(data));
  };

  private addUpdatePricingListener = () => {
    this.hubConnection.on(SignalrMethodName.updateQuotePricing, data => this.updatePricingProgressReceived$.emit(data));
  };

  private addEditQuoteListener = () => {
    this.hubConnection.on(SignalrMethodName.editQuote, data => this.editQuoteProgressReceived$.emit(data));
  };

  private addEditQuoteListenerForCalculateTotals = () => {
    this.hubConnection.on(SignalrMethodName.editQuote, data => this.editQuoteForCalculateTotalsProgressReceived$.emit(data));
  };

  private addStartGoOnlineListener = () => {
    this.hubConnection.on(SignalrMethodName.startGoOnlineSync, data => this.startedGoOnlineSyncProgressReceived$.emit(data));
  };

  private addReceiveOnlineUpdatesListener = () => {
    this.hubConnection.on(SignalrMethodName.receiveOnlineUpdates, data => this.receiveOnlineUpdatesProgressReceived$.emit(data));
  };

  private addImportQuoteOrOrderDataSetListener = () => {
    this.hubConnection.on(SignalrMethodName.importQuotesOrOrders, data => this.importQuotesOrOrdersData$.emit(data));
  };

  private addGenerateQuoteListener = () => {
    this.hubConnection.on(SignalrMethodName.generateQuote, data => this.generateQuoteProgressReceived$.emit(data));
  };

  closeConnection(callback?: (...args: any[]) => any) {
    if (!this.hubConnection) {
      return;
    }
    this.hubConnection.stop().then(() => {
      if (callback) {
        callback();
      }
    });
  }

  /**
   * Sets the path to signalR
   */
  private setPath(fullUrl?) {
    if (fullUrl) {
      this.path = fullUrl;
      return;
    }
    if (this.appStateService.state.isElectron) {
      if (this.appStateService.state.isOnline) {
        this.path = `${environment.webRoot}${environment.signalR}`;
      } else {
        this.path = `${environment.offlineApiRoot}${environment.signalR}`;
      }
    } else {
      this.path = environment.signalR;
    }
  }
}
